import { parseEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  EntityKindFilter,
  EntityKindPicker,
  EntityLifecycleFilter,
  EntityLifecyclePicker,
  EntityOwnerFilter,
  EntityOwnerPicker,
  EntityTagFilter,
  EntityTagPicker,
  EntityTypeFilter,
  EntityTypePicker,
  EntityUserFilter,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { ExplorerAutocompleteField } from '../../entity-explorer/shared';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import {
  catalogIndexColumnLabels,
  CatalogIndexColumnId,
} from './useCatalogIndexState';

const scopeOptions = [
  { label: 'All', value: 'all' },
  { label: 'Owned', value: 'owned' },
  { label: 'Starred', value: 'starred' },
] as const;

const formatFilterValue = (value: string) =>
  value
    .split(/[-_]/g)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatOwnerRef = (value: string) => {
  try {
    return formatFilterValue(
      parseEntityRef(value, { defaultKind: 'Group' }).name,
    );
  } catch {
    return formatFilterValue(value);
  }
};

const getFilterPanelSx = (theme: Theme) => {
  const tokens = getThemeTokens(theme);

  return {
    '& .CatalogIndexFilters-grid': {
      display: 'grid',
      columnGap: 8,
      rowGap: 10,
      gridTemplateColumns: {
        lg: 'repeat(6, minmax(0, 1fr))',
        md: 'repeat(3, minmax(0, 1fr))',
        sm: 'repeat(2, minmax(0, 1fr))',
        xs: 'minmax(0, 1fr)',
      },
    },
    '& .CatalogIndexFilters-grid > *': {
      minWidth: 0,
      width: '100%',
    },
    '& .CatalogIndexFilters-grid [class*="BackstageSelect-root"], & .CatalogIndexFilters-grid [class*="CatalogReactEntityOwnerPicker-root"], & .CatalogIndexFilters-grid [class*="CatalogReactEntityAutocompletePicker-root"]':
      {
        paddingBottom: '0 !important',
        paddingTop: '0 !important',
        width: '100%',
      },
    '& .CatalogIndexFilters-grid .MuiFormControl-root': {
      width: '100%',
    },
    '& [class*="BackstageSelect-formControl"]': {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      minWidth: 0,
      width: '100%',
    },
    '& [class*="BackstageSelect-formLabel"]': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.04em',
      lineHeight: 1.2,
      marginBottom: theme.spacing(0.5),
      position: 'static',
      textTransform: 'uppercase',
      transform: 'none',
    },
    '& [class*="BackstageAutocomplete-label"]': {
      color: `${tokens.textPrimary} !important`,
      display: 'flex',
      flexDirection: 'column',
      fontSize: 12,
      fontWeight: 700,
      gap: theme.spacing(0.5),
      letterSpacing: '0.04em',
      margin: 0,
      minWidth: 0,
      textTransform: 'uppercase',
      width: '100%',
    },
    '& [class*="BackstageAutocomplete-label"] > span:first-of-type': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.2,
      position: 'static',
    },
    '& [class*="MuiSelect-select"], & [role="button"]': {
      fontSize: 14,
    },
    '& [class*="BackstageSelectInputBase-root"], & [class*="BackstageAutocompleteBase-inputRoot"]':
      {
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.md}px`,
        boxShadow: 'none',
        marginTop: '0 !important',
        minHeight: 40,
      },
    '& [class*="BackstageSelectInputBase-root"] .MuiSelect-select': {
      alignItems: 'center',
      display: 'flex',
      minHeight: '32px !important',
      paddingLeft: '14px !important',
      paddingBottom: '0 !important',
      paddingRight: '38px !important',
      paddingTop: '0 !important',
    },
    '& [class*="BackstageAutocompleteBase-inputRoot"]': {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: 40,
      paddingBottom: theme.spacing(0.25),
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(4.25),
      paddingTop: theme.spacing(0.25),
      rowGap: theme.spacing(0.25),
    },
    '& [class*="BackstageAutocompleteBase-inputRoot"] fieldset': {
      borderColor: `${getSurfaceBorder(theme)} !important`,
    },
    '& [data-catalog-autocomplete-field]': {
      minWidth: 0,
      position: 'relative',
    },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-tag': {
      margin: theme.spacing(0.25, 0.5, 0.25, 0),
      maxWidth: 'calc(100% - 8px)',
    },
    '& [data-catalog-autocomplete-field] .MuiChip-root': {
      backgroundColor: getSurfaceColor(theme, 'raised'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: 999,
      height: 24,
    },
    '& [data-catalog-autocomplete-field] .MuiChip-label': {
      color: tokens.textPrimary,
      fontSize: 12,
      fontWeight: 600,
      paddingLeft: 10,
      paddingRight: 6,
    },
    '& [data-catalog-autocomplete-field] .MuiChip-deleteIcon': {
      color: tokens.textMuted,
      fontSize: 16,
      marginLeft: 0,
      marginRight: 4,
    },
    '& [class*="BackstageAutocompleteBase-input"]': {
      fontSize: 14,
      minWidth: 36,
      padding: '2px 0 0 !important',
    },
    '& [class*="BackstageAutocompleteBase-popupIndicator"]': {
      color: `${tokens.textSecondary} !important`,
      marginRight: theme.spacing(0.5),
    },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-endAdornment': {
      right: 8,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& [data-catalog-autocomplete-field][data-has-value="false"]::after': {
      color: alpha(tokens.textMuted, 0.9),
      content: 'attr(data-placeholder)',
      fontSize: 14,
      left: 14,
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& [data-catalog-autocomplete-field][data-has-value="false"]:focus-within::after':
      {
        content: 'none',
      },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-input::placeholder': {
      color: 'transparent',
    },
  };
};

const ScopeButton = ({
  active,
  disabled = false,
  label,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) => {
  const theme = useTheme();

  return (
    <ButtonBase
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      sx={{
        backgroundColor: active
          ? alpha(theme.palette.primary.main, 0.12)
          : getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${
          active
            ? alpha(theme.palette.primary.main, 0.32)
            : getSurfaceBorder(theme)
        }`,
        borderRadius: 999,
        color: active ? theme.palette.primary.main : theme.palette.text.primary,
        minHeight: 30,
        px: 1.25,
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.shorter },
        ),
      }}
    >
      <Typography
        sx={{ fontSize: 12.5, fontWeight: active ? 700 : 600, lineHeight: 1 }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
};

export const CatalogIndexControls = ({
  columnIds,
  resetVisibleColumns,
  searchText,
  setSearchText,
  toggleVisibleColumn,
  visibleColumns,
}: {
  columnIds: CatalogIndexColumnId[];
  resetVisibleColumns: () => void;
  searchText: string;
  setSearchText: (value: string) => void;
  toggleVisibleColumn: (columnId: CatalogIndexColumnId) => void;
  visibleColumns: CatalogIndexColumnId[];
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const identityApi = useApi(identityApiRef);
  const { filters, updateFilters } = useEntityList();
  const { starredEntities } = useStarredEntities();

  const { loading: loadingOwnership, value: ownershipEntityRefs = [] } =
    useAsync(async () => {
      const identity = await identityApi.getBackstageIdentity();
      return identity.ownershipEntityRefs;
    }, [identityApi]);

  const ownerValues =
    (filters.owners as { values?: string[] } | undefined)?.values ?? [];
  const lifecycleValues = filters.lifecycles?.values ?? [];
  const tagValues =
    (filters.tags as { values?: string[] } | undefined)?.values ?? [];
  const typeValues = filters.type?.getTypes?.() ?? [];
  const selectedKind = filters.kind?.value;
  const selectedScope =
    (filters.user as { value?: 'all' | 'owned' | 'starred' } | undefined)
      ?.value ?? 'all';

  const setScope = (scope: 'all' | 'owned' | 'starred') => {
    updateFilters({
      user:
        scope === 'owned'
          ? EntityUserFilter.owned(ownershipEntityRefs)
          : scope === 'starred'
            ? EntityUserFilter.starred(Array.from(starredEntities))
            : EntityUserFilter.all(),
    });
  };

  const clearAllFilters = () => {
    setSearchText('');
    setFiltersExpanded(false);
    updateFilters({
      error: undefined,
      kind: new EntityKindFilter('component', 'Component'),
      lifecycles: undefined,
      owners: undefined,
      tags: undefined,
      type: undefined,
      user: EntityUserFilter.all(),
      orphan: undefined,
    });
  };

  const activeFilterChips = useMemo(
    () => [
      ...(selectedKind && selectedKind !== 'component'
        ? [
            {
              key: `kind-${selectedKind}`,
              label: `Kind: ${formatFilterValue(
                filters.kind?.label ?? selectedKind,
              )}`,
              onDelete: () =>
                updateFilters({
                  kind: new EntityKindFilter('component', 'Component'),
                }),
            },
          ]
        : []),
      ...(selectedScope === 'owned'
        ? [
            {
              key: 'scope-owned',
              label: 'Owned',
              onDelete: () => setScope('all'),
            },
          ]
        : []),
      ...(selectedScope === 'starred'
        ? [
            {
              key: 'scope-starred',
              label: 'Starred',
              onDelete: () => setScope('all'),
            },
          ]
        : []),
      ...typeValues.map(type => ({
        key: `type-${type}`,
        label: `Type: ${formatFilterValue(type)}`,
        onDelete: () => {
          const nextValues = typeValues.filter(value => value !== type);
          updateFilters({
            type: nextValues.length
              ? new EntityTypeFilter(nextValues)
              : undefined,
          });
        },
      })),
      ...ownerValues.map(owner => ({
        key: `owner-${owner}`,
        label: `Owner: ${formatOwnerRef(owner)}`,
        onDelete: () => {
          const nextValues = ownerValues.filter(value => value !== owner);
          updateFilters({
            owners: nextValues.length
              ? new EntityOwnerFilter(nextValues)
              : undefined,
          });
        },
      })),
      ...lifecycleValues.map(lifecycle => ({
        key: `lifecycle-${lifecycle}`,
        label: `Lifecycle: ${formatFilterValue(lifecycle)}`,
        onDelete: () => {
          const nextValues = lifecycleValues.filter(
            value => value !== lifecycle,
          );
          updateFilters({
            lifecycles: nextValues.length
              ? new EntityLifecycleFilter(nextValues)
              : undefined,
          });
        },
      })),
      ...tagValues.map(tag => ({
        key: `tag-${tag}`,
        label: `Tag: ${tag}`,
        onDelete: () => {
          const nextValues = tagValues.filter(value => value !== tag);
          updateFilters({
            tags: nextValues.length
              ? new EntityTagFilter(nextValues)
              : undefined,
          });
        },
      })),
      ...(filters.orphan?.value
        ? [
            {
              key: 'status-orphan',
              label: 'Status: Orphaned',
              onDelete: () => updateFilters({ orphan: undefined }),
            },
          ]
        : []),
      ...(filters.error?.value
        ? [
            {
              key: 'status-error',
              label: 'Status: Errors',
              onDelete: () => updateFilters({ error: undefined }),
            },
          ]
        : []),
    ],
    [
      filters.error?.value,
      filters.kind?.label,
      filters.orphan?.value,
      lifecycleValues,
      ownerValues,
      selectedKind,
      selectedScope,
      starredEntities,
      tagValues,
      typeValues,
      updateFilters,
      ownershipEntityRefs,
    ],
  );
  const activeFilterCount = activeFilterChips.length;
  const hasCollapsedFilterSummary =
    !filtersExpanded && activeFilterChips.length > 0;

  return (
    <Paper
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        px: { md: 2, xs: 1.5 },
        pb: hasCollapsedFilterSummary
          ? { md: 1, xs: 0.875 }
          : { md: 1.5, xs: 1.25 },
        pt: { md: 1.5, xs: 1.25 },
        ...getFilterPanelSx(theme),
      }}
    >
      <Stack spacing={hasCollapsedFilterSummary ? 1 : 1.25}>
        <Stack
          alignItems={{ lg: 'center', xs: 'stretch' }}
          direction={{ lg: 'row', xs: 'column' }}
          spacing={1}
        >
          <OutlinedInput
            fullWidth
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            placeholder="Search catalog entities"
            startAdornment={
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            }
            endAdornment={
              searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear catalog search"
                    edge="end"
                    onClick={() => setSearchText('')}
                    size="small"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              flex: 1,
              minHeight: 40,
              minWidth: 0,
            }}
          />

          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            justifyContent={{ lg: 'flex-end', xs: 'flex-start' }}
            spacing={0.75}
            useFlexGap
          >
            {scopeOptions.map(option => (
              <ScopeButton
                key={option.value}
                active={selectedScope === option.value}
                disabled={option.value === 'owned' && loadingOwnership}
                label={option.label}
                onClick={() => setScope(option.value)}
              />
            ))}

            <Button
              onClick={() => setFiltersExpanded(previous => !previous)}
              startIcon={<TuneOutlinedIcon />}
              size="small"
              variant={filtersExpanded ? 'contained' : 'outlined'}
            >
              {activeFilterCount > 0
                ? `Filters (${activeFilterCount})`
                : 'Filters'}
            </Button>

            <Button
              onClick={event => setAnchorEl(event.currentTarget)}
              size="small"
              variant="outlined"
            >
              Columns
            </Button>
          </Stack>
        </Stack>

        {activeFilterChips.length > 0 && (
          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            spacing={hasCollapsedFilterSummary ? 0.5 : 0.75}
            useFlexGap
          >
            {activeFilterChips.map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={chip.onDelete}
                size="small"
                sx={{
                  backgroundColor: getSurfaceColor(theme, 'subtle'),
                  border: `1px solid ${getSurfaceBorder(theme)}`,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  height: 26,
                }}
              />
            ))}

            <Button
              onClick={clearAllFilters}
              size="small"
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              Clear all
            </Button>
          </Stack>
        )}

        <Collapse in={filtersExpanded}>
          <Stack spacing={1.25}>
            <Divider sx={{ borderColor: getSurfaceBorder(theme) }} />

            <Box className="CatalogIndexFilters-grid">
              <Box
                sx={{
                  gridColumn: {
                    lg: 'span 2',
                    md: 'span 2',
                    sm: 'span 2',
                    xs: 'auto',
                  },
                }}
              >
                <ExplorerAutocompleteField
                  hasValue={ownerValues.length > 0}
                  placeholder="All owners"
                >
                  <EntityOwnerPicker />
                </ExplorerAutocompleteField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <EntityKindPicker initialFilter="component" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <EntityTypePicker />
              </Box>
              <ExplorerAutocompleteField
                hasValue={lifecycleValues.length > 0}
                placeholder="All lifecycles"
              >
                <EntityLifecyclePicker />
              </ExplorerAutocompleteField>
              <ExplorerAutocompleteField
                hasValue={tagValues.length > 0}
                placeholder="All tags"
              >
                <EntityTagPicker />
              </ExplorerAutocompleteField>
            </Box>
          </Stack>
        </Collapse>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
      >
        {columnIds.map(id => (
          <MenuItem
            key={id}
            onClick={() => toggleVisibleColumn(id)}
            sx={{ minWidth: 220 }}
          >
            <Checkbox checked={visibleColumns.includes(id)} size="small" />
            <Typography sx={{ fontSize: 14 }}>
              {catalogIndexColumnLabels[id]}
            </Typography>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            resetVisibleColumns();
            setAnchorEl(null);
          }}
        >
          Reset columns
        </MenuItem>
      </Menu>
    </Paper>
  );
};
